import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney, formatDateShort } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/bookingConstants";
import { FieldSelect } from "@/components/ui/field-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ExpenseFormDrawer from "@/components/finance/ExpenseFormDrawer";
import { ExportButton } from "@/components/ExportButton";
import ExpenseDetailsDrawer from "@/components/finance/ExpenseDetailsDrawer";
import { Plus, Search, Loader2, Receipt, Settings2, Paperclip } from "lucide-react";

const QUICK = [{ k: "aujourdhui", l: "Aujourd'hui" }, { k: "semaine", l: "Cette semaine" }, { k: "mois", l: "Ce mois" }];

export default function Depenses() {
  const { orgId, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState("mois");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [method, setMethod] = useState("");
  const [status, setStatus] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);

  useEffect(() => {
    if (params.get("new") && can("expenses.manage")) {
      setFormOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
  }, [params, can, setParams]);

  const { data: catData } = useQuery({
    queryKey: ["expense-categories", orgId],
    queryFn: async () => (await api.get("/api/expense-categories", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });
  const cats = catData?.categories || [];

  const { data, isLoading } = useQuery({
    queryKey: ["expenses", orgId, period, q, category, method, status],
    queryFn: async () => (await api.get("/api/expenses", { params: {
      organization_id: orgId, period, q: q || undefined, category_id: category || undefined,
      payment_method: method || undefined, status: status || undefined } })).data,
    enabled: !!orgId,
  });

  const currency = data?.currency || "MAD";
  const s = data?.summary || {};
  const rows = data?.expenses || [];
  const m = (c) => formatMoney(c || 0, currency);

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="depenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Dépenses</h1>
        <div className="flex gap-2">
          {can("finance.view") && <ExportButton kind="expenses" orgId={orgId} testid="expenses-export-button" />}
          {can("expenses.manage") && (
            <button onClick={() => setCatsOpen(true)} data-testid="manage-categories-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><Settings2 className="h-4 w-4" /> Catégories</button>
          )}
          {can("expenses.manage") && (
            <button onClick={() => setFormOpen(true)} data-testid="new-expense-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle dépense</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Summary label="Dépenses aujourd'hui" value={m(s.today_cents)} testid="summary-today" />
        <Summary label="Dépenses ce mois" value={m(s.month_cents)} testid="summary-month" />
        <Summary label="Espèces (mois)" value={m(s.by_method?.cash)} testid="summary-cash" />
        <Summary label="Carte / Virement (mois)" value={m((s.by_method?.card || 0) + (s.by_method?.transfer || 0) + (s.by_method?.other || 0))} testid="summary-noncash" />
      </div>

      {(s.top_categories || []).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="top-categories">
          {s.top_categories.map((c) => (
            <span key={c.name} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{c.name} · {m(c.amount_cents)}</span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          {QUICK.map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${period === p.k ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}>{p.l}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="expense-search"
            className="w-56 rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <FieldSelect testId="filter-category" className="w-44" value={category || "ALL"} onChange={(v) => setCategory(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Toute catégorie" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]} />
        <FieldSelect testId="filter-method" className="w-40" value={method || "ALL"} onChange={(v) => setMethod(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Tout paiement" }, ...Object.entries(PAYMENT_METHODS).map(([k, v]) => ({ value: k, label: v }))]} />
        <FieldSelect testId="filter-status" className="w-36" value={status || "ALL"} onChange={(v) => setStatus(v === "ALL" ? "" : v)}
          options={[{ value: "ALL", label: "Tout statut" }, { value: "VALID", label: "Valide" }, { value: "CANCELLED", label: "Annulée" }]} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="expenses-empty">
          <Receipt className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-base font-semibold text-slate-700">Aucune dépense sur cette période</p>
          <p className="mt-1 text-sm text-slate-400">Enregistrez vos dépenses pour suivre votre rentabilité en temps réel.</p>
          {can("expenses.manage") && <button onClick={() => setFormOpen(true)} data-testid="expenses-empty-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle dépense</button>}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white lg:block">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>{["Référence", "Date", "Catégorie", "Montant", "Paiement", "Bénéficiaire", "Saisi par", "Statut"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((e) => (
                  <tr key={e.id} onClick={() => setDetailId(e.id)} data-testid={`expense-row-${e.id}`} className={`cursor-pointer hover:bg-slate-50 ${e.status === "CANCELLED" ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 font-mono text-slate-700">{e.expense_reference}{e.receipt_file_id && <Paperclip className="ml-1 inline h-3 w-3 text-slate-400" />}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDateShort(e.expense_date)}</td>
                    <td className="px-4 py-3 text-slate-700">{e.category_name}</td>
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{m(e.amount_cents)}</td>
                    <td className="px-4 py-3 text-slate-600">{PAYMENT_METHODS[e.payment_method] || e.payment_method}</td>
                    <td className="px-4 py-3 text-slate-600">{e.supplier_or_recipient || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.created_by_name}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${e.status === "CANCELLED" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>{e.status === "CANCELLED" ? "Annulée" : "Valide"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="space-y-2 lg:hidden">
            {rows.map((e) => (
              <button key={e.id} onClick={() => setDetailId(e.id)} data-testid={`expense-card-${e.id}`} className={`flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-left ${e.status === "CANCELLED" ? "opacity-50" : ""}`}>
                <div>
                  <p className="font-medium text-slate-800">{e.category_name}</p>
                  <p className="text-xs text-slate-400">{e.expense_reference} · {formatDateShort(e.expense_date)} · {PAYMENT_METHODS[e.payment_method]}</p>
                </div>
                <span className="font-mono font-semibold text-slate-900">{m(e.amount_cents)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <ExpenseFormDrawer open={formOpen} onOpenChange={setFormOpen} currency={currency} />
      {detailId && <ExpenseDetailsDrawer expenseId={detailId} open={!!detailId} onOpenChange={() => setDetailId(null)} />}
      <CategoriesDialog open={catsOpen} onOpenChange={setCatsOpen} cats={cats} />
    </div>
  );
}

function CategoriesDialog({ open, onOpenChange, cats }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: async () => (await api.post("/api/expense-categories", { name }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Catégorie créée"); setName(""); qc.invalidateQueries({ queryKey: ["expense-categories"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const upd = useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/api/expense-categories/${id}`, patch, { params: { organization_id: orgId } })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expense-categories"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="categories-dialog">
        <DialogHeader>
          <DialogTitle>Catégories de dépenses</DialogTitle>
          <DialogDescription className="sr-only">Créer, renommer ou désactiver les catégories de dépenses.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nouvelle catégorie" data-testid="new-category-input"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending} data-testid="add-category-button"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">Ajouter</button>
          </div>
          <div className="divide-y divide-slate-100">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2" data-testid={`category-row-${c.id}`}>
                <input defaultValue={c.name} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== c.name) upd.mutate({ id: c.id, patch: { name: e.target.value.trim() } }); }}
                  data-testid={`category-name-${c.id}`} className={`flex-1 rounded-md border border-transparent px-2 py-1 text-sm hover:border-slate-200 ${c.is_active ? "text-slate-800" : "text-slate-400 line-through"}`} />
                <button onClick={() => upd.mutate({ id: c.id, patch: { is_active: !c.is_active } })} data-testid={`category-toggle-${c.id}`}
                  className={`ml-2 rounded-md px-2 py-1 text-xs font-medium ${c.is_active ? "text-rose-600 hover:bg-rose-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
                  {c.is_active ? "Désactiver" : "Réactiver"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Summary = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-1.5 font-mono text-xl font-bold text-slate-900">{value}</p>
  </div>
);
